import { useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import PodcastsIcon from '@mui/icons-material/Podcasts'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import type { Podcast, PodcastEpisode } from '@/types/api.types'
import { useDeleteEpisode } from '@/hooks/usePodcasts'
import { ImportAudioDialog } from './ImportAudioDialog'

interface PodcastEpisodeSectionProps {
  /** The project owning these episodes — used for navigation and delete mutation. */
  projectId: number
  /** Map of podId → Podcast (with nested podcastEpisodes) from useProjectPodcasts(). */
  podcastsMap: Record<number, Podcast>
  /** The project's target language — used as the default language for imported audio. */
  projectLanguage: string
}

/**
 * PodcastEpisodeSection renders the podcasts block inside the project-detail page,
 * mirroring the YouTubeVideoSection layout (src/components/youtube/).
 *
 * - Shows each podcast as a header row (avatar + name + episode count).
 * - Lists each episode as an individual card beneath its podcast header.
 * - Episode cards always render an image area (episode artwork → podcast artwork → placeholder)
 *   so all cards in a row share the same height.
 * - Clicking an episode opens the player in focus mode via ?focus=true.
 * - Each card has an inline delete icon that opens a MUI Dialog for confirmation.
 * - Zero-state: shows an empty-state prompt when podcastsMap is empty.
 */
export const PodcastEpisodeSection = ({ projectId, podcastsMap, projectLanguage }: PodcastEpisodeSectionProps) => {
  const { t } = useTranslation('projects')
  const navigate = useNavigate()

  const [pendingDelete, setPendingDelete] = useState<{
    episode: PodcastEpisode
    podcastId: number
  } | null>(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const { mutate: deleteEpisode, isPending: isDeleting } = useDeleteEpisode()

  const handleDeleteConfirm = () => {
    if (!pendingDelete) return
    deleteEpisode(
      { projectId, episodeId: pendingDelete.episode.id },
      { onSuccess: () => setPendingDelete(null) }
    )
  }

  const handleDeleteCancel = () => {
    if (isDeleting) return
    setPendingDelete(null)
  }

  const handleOpenPlayer = (episode: PodcastEpisode) => {
    navigate(`/player/${projectId}/${episode.id}?focus=true`)
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
  }

  const formatDate = (timestamp: number) => new Date(timestamp * 1000).toLocaleDateString()

  const podcasts = Object.values(podcastsMap)

  return (
    <Box mb={4}>
      {/* Section heading + Add Episodes button */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <PodcastsIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            {t('podcasts')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/podcasts')}
          >
            {t('addEpisodes')}
          </Button>
        </Stack>
      </Box>

      {/* Zero state */}
      {podcasts.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="20vh"
          textAlign="center"
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('noPodcastsYet')}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {t('searchForPodcasts')}
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setImportDialogOpen(true)}
            >
              {t('importAudio', 'Import Audio')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/podcasts')}
            >
              {t('searchPodcasts')}
            </Button>
          </Stack>
        </Box>
      )}

      {/* Per-podcast sections */}
      {podcasts.map((podcast) => (
        <Box key={podcast.podId} mb={4}>
          {/* Podcast header row */}
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Avatar
              src={podcast.imageUrl || undefined}
              alt={podcast.name}
              sx={{ width: 32, height: 32 }}
            >
              <PodcastsIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6" component="h3">
              {podcast.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({podcast.podcastEpisodes?.length ?? 0})
            </Typography>
          </Stack>

          {/* Episode card grid — mobile-first, no horizontal scroll at 375px */}
          <Grid container spacing={2}>
            {(podcast.podcastEpisodes ?? []).map((episode) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={episode.id}>
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleOpenPlayer(episode)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleOpenPlayer(episode)
                  }}
                  aria-label={t('openEpisodePlayer', { name: episode.name })}
                >
                  {/* Always render image area to keep consistent card heights across row */}
                  {episode.imageUrl || podcast.imageUrl ? (
                    <CardMedia
                      component="img"
                      image={episode.imageUrl || podcast.imageUrl}
                      alt={episode.name}
                      sx={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '1/1',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PodcastsIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, pr: 5 }}>
                    <Typography variant="body1" fontWeight="medium" gutterBottom noWrap>
                      {episode.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDuration(episode.duration)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(episode.datePublished)}
                      </Typography>
                    </Box>
                  </CardContent>

                  {/* Delete affordance — top-right, does not disrupt card layout */}
                  <Tooltip title={t('removeEpisode')}>
                    <IconButton
                      aria-label={t('removeEpisode')}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        setPendingDelete({ episode, podcastId: podcast.podId })
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'background.paper',
                        '&:hover': { color: 'error.main' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Confirmation dialog */}
      <Dialog
        open={pendingDelete !== null}
        onClose={handleDeleteCancel}
        aria-labelledby="remove-episode-dialog-title"
        aria-describedby="remove-episode-dialog-description"
      >
        <DialogTitle id="remove-episode-dialog-title">
          {t('removeEpisodeTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-episode-dialog-description">
            {t('removeEpisodeMessage', { name: pendingDelete?.episode.name ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting} autoFocus>
            {t('removeEpisodeCancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {t('removeEpisodeConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <ImportAudioDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        projectId={projectId}
        projectLanguage={projectLanguage}
      />
    </Box>
  )
}
